// @ts-check

// Vendor imports
const Cookies = require('js-cookie')
// Lib imports
const globalPageContext = require('client/lib/business-events/page-context')
const cookieLib = require('client/lib/cookie')

// Version the `user_identified` cookie since sometimes we need to
// trigger users to re-identify after code-changes.
const USER_IDENTIFIED_VERSION = '6'

const BETA_PATHS = [
  '/u/',
  '/p/'
]

// Only logged-in users will be identified
let userIdentified = false;

/** @typedef { import('./types.d').User } User */

/**
 * @typedef {Object} SegmentSDK
 * @prop {function} identify
 * @prop {function} page
 * @prop {function} track
 * @see https://segment.com/docs/sources/website/analytics.js/
 */

 /** @type {SegmentSDK} */
// @ts-ignore Assume that segment exists on the window object
const segmentSdk = typeof window === 'object' ? window.analytics : {
  identify() {},
  page() {},
  track() {}
}

/**
 * A thin wrapper around Segment SDK's `identify` method
 * @param {User} user
 * @see https://segment.com/docs/spec/identify/
 * @see https://segment.com/docs/sources/website/analytics.js/#identify
 */
async function identifyUser(user) {
  // The Segment SDK is sometimes blocked by the user-agent
  if (!segmentSdk) {
    return
  }
  // Users that have already been identified do not need to be re-identified
  if (Cookies.get('user_identified') === USER_IDENTIFIED_VERSION) {
    userIdentified = true
    return
  }
  // Logged-out users have a randomly-generated anonymous ID
  if (!user.loggedIn) {
    // Beta landing visitors special
    const pageName = globalPageContext.getPageContext()['page_name']
    if (BETA_PATHS.find(path => window.location.href.includes(path)) || pageName === 'LOHP') {
      segmentSdk.identify(user.id)
      Cookies.set(
        'user_identified',
        USER_IDENTIFIED_VERSION,
        cookieLib.setAttributes('user_identified')
      )
      userIdentified = true
    }
    return
  }

  // Convert providers Object to comma-separated keys string
  const providers = Object.keys(user.providers).join(',')

  // Convert experiments Object into a comma-separated key=value string
  const testGroups = Object.entries(user.experiments)
  .map(pair => pair.join('='))
  .join(',')

  segmentSdk.identify(user.id, {
    'access_granted_at': user['access_granted_at'],
    'birthday': user.birthday,
    'category': user.category,
    'created_at': user['created_at'],
    'display_name': user['display_name'],
    'gender': user.gender,
    'name': user['display_name'],
    'providers': providers,
    'signup_context': user['signup_context'],
    'test_groups': testGroups,
    'username': user.username,
    'user_type': user['user_type']
  })

  Cookies.set(
    'user_identified',
    USER_IDENTIFIED_VERSION,
    cookieLib.setAttributes('user_identified')
  )
  userIdentified = true;
}


/**
 * A thin wrapper around Segment SDK's `track` method
 * @param {string} eventName
 * @param {Object} [properties]
 * @see https://segment.com/docs/spec/track/
 * @see https://segment.com/docs/sources/website/analytics.js/#track
 */
async function track(eventName, properties) {
  if (!segmentSdk || !userIdentified) {
    return
  }
  segmentSdk.track(eventName, properties)
}

/**
 * A thin wrapper around Segment SDK's `page` method
 * @param {string} pageName
 * @param {Object} [properties]
 * @see https://segment.com/docs/spec/page/
 * @see https://segment.com/docs/sources/website/analytics.js/#page
 */
async function page(pageName, properties = {}) {
  if (!segmentSdk || !userIdentified) {
    return
  }
  segmentSdk.page(pageName, properties)
}

module.exports = {
  identifyUser,
  track,
  page
}
